<script setup lang="ts">
import { computed, onMounted, toRef } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useAnalysisQuery } from "@/composables/useAnalysisQuery";
import type { ChartCard, GeoDistributionData } from "@/types";
import { useAnalysisContext, useCardPopulation } from "../analysisData";
import ResultsGeoMapChart, { type GeoZone } from "../Charts/ResultsGeoMapChart.vue";
import AnalysisCardShell from "./AnalysisCardShell.vue";
import PopulationSwitch from "./PopulationSwitch.vue";

const props = defineProps({
  card: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});

const card = toRef(props, "card");
const store = useResultsV2Store();
const { allQuestionCards } = storeToRefs(store);
const { weighted, availableCards } = useAnalysisContext();
const { choices, selected, filters, color: populationColor } = useCardPopulation(card);

onMounted(() => store.fetchAllQuestionCards());

const questionId = computed<number | null>(() => props.card.content?.question_id || null);
const country = computed<string>(() => props.card.content?.country || "FR");
const metricQuestionId = computed<number | null>(() => props.card.content?.metric_question_id || null);
const metricCodes = computed<string[]>(() => props.card.content?.metric_codes || []);

function metaFor(id: number | null): ChartCard | null {
  if (!id) return null;
  const match = (cards: ChartCard[]) => cards.find((c) => (c.questions || []).some((q) => q.id === id)) || null;
  return match(allQuestionCards.value as ChartCard[]) || match(availableCards.value as ChartCard[]);
}

const questionMeta = computed(() => metaFor(questionId.value));
const metricMeta = computed(() => metaFor(metricQuestionId.value));

const SHARE_TYPES = ["q_single", "q_multiple", "q_image_choice", "q_image_choice_multiple", "q_hidden"];
const isShareMetric = computed(() => !!metricMeta.value && SHARE_TYPES.includes(metricMeta.value.type || ""));

const effectiveMetricId = computed(() => {
  if (!metricQuestionId.value) return null;
  if (isShareMetric.value && !metricCodes.value.length) return null;
  return metricQuestionId.value;
});

const body = computed(() => {
  if (!questionId.value) return null;
  return {
    question_id: questionId.value,
    country: country.value,
    metric_question_id: effectiveMetricId.value,
    metric_codes: isShareMetric.value ? metricCodes.value : [],
    filters: filters.value,
  };
});

const { result, loading, state } = useAnalysisQuery<GeoDistributionData>("geo_distribution", body);

const respondents = computed(() => {
  if (!result.value) return null;
  return weighted.value ? result.value.weighted_respondents : result.value.respondents;
});

const ignored = computed(() => (result.value ? (weighted.value ? result.value.weighted_ignored : result.value.ignored) : 0));

const respondentsHint = computed(() => {
  const base = "Respondents with a zipcode on the map.";
  return ignored.value > 0 ? `${base} ${ignored.value} answered a zipcode outside the country and are ignored.` : base;
});

const zones = computed<GeoZone[]>(() =>
  (result.value?.zones || []).map((zone) => ({
    code: zone.code,
    respondents: weighted.value ? zone.weighted_nb : zone.nb,
    metricBase: weighted.value ? zone.weighted_metric_base : zone.metric_base,
    value: weighted.value ? zone.weighted_value : zone.value,
  })),
);

const accent = computed(() => props.card.options?.color || populationColor.value);

const cardState = computed(() => state(zones.value.length > 0));
</script>

<template>
  <AnalysisCardShell
    :card="card"
    :readonly="readonly"
    :respondents="respondents"
    :weighted="weighted"
    :respondents-hint="respondentsHint"
    description="Respondents by area, from the zipcodes they gave. Darker areas hold more respondents, or a higher value when the map is colored by a question."
    :state="cardState"
    state-message="Select a zipcode question to draw the map.">
    <template #subtitle>
      <p v-if="questionMeta" class="text-xs text-muted-foreground">
        {{ questionMeta.name }}
        <template v-if="metricMeta && effectiveMetricId"> · colored by {{ metricMeta.name }}</template>
      </p>
    </template>
    <template #toolbar>
      <PopulationSwitch v-if="choices.length > 1" :card="card" :populations="choices" :selected="selected" />
    </template>

    <div :class="{ 'opacity-50': loading }">
      <ResultsGeoMapChart
        :zones="zones"
        :country="country"
        :has-metric="result?.has_metric && !!effectiveMetricId"
        :metric-kind="result?.metric_kind || null"
        :color="accent"
        :min-base="card.content?.min_base ?? 5" />
    </div>
  </AnalysisCardShell>
</template>
